import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Upload a photo to Firebase Storage
 * @param orgId - Organization ID for scoping the upload
 * @param inspectionId - Inspection ID for organizing files
 * @param photoData - Base64 data URL of the photo
 * @param index - Index of the photo in the collection
 * @returns Promise<string> - Download URL of the uploaded photo
 */
export async function uploadPhoto(
  orgId: string,
  inspectionId: string,
  photoData: string,
  index: number
): Promise<string> {
  try {
    // Convert base64 to blob
    const blob = await dataURLToBlob(photoData);

    // Create a unique filename
    const timestamp = Date.now();
    const filename = `${timestamp}-${index}.jpg`;
    const storagePath = `organizations/${orgId}/inspections/${inspectionId}/photos/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: 'image/jpeg',
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload photo:', error);
    throw new Error('Failed to upload photo');
  }
}

/**
 * Upload a signature to Firebase Storage
 * @param orgId - Organization ID for scoping the upload
 * @param inspectionId - Inspection ID for organizing files
 * @param signatureData - Base64 data URL of the signature
 * @param fieldId - Field ID for the signature
 * @returns Promise<string> - Download URL of the uploaded signature
 */
export async function uploadSignature(
  orgId: string,
  inspectionId: string,
  signatureData: string,
  fieldId: string
): Promise<string> {
  try {
    // Convert base64 to blob
    const blob = await dataURLToBlob(signatureData);

    // Create a unique filename
    const timestamp = Date.now();
    const filename = `${fieldId}-${timestamp}.png`;
    const storagePath = `organizations/${orgId}/inspections/${inspectionId}/signatures/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: 'image/png',
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload signature:', error);
    throw new Error('Failed to upload signature');
  }
}

/**
 * Delete a file from Firebase Storage
 * @param fileUrl - Full download URL of the file to delete
 */
export async function deleteFile(fileUrl: string): Promise<void> {
  try {
    // Extract storage path from URL
    const storageRef = ref(storage, fileUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Failed to delete file:', error);
    // Don't throw - deletion failures are non-critical
  }
}

/**
 * Convert a data URL to a Blob
 * @param dataURL - Base64 data URL
 * @returns Promise<Blob>
 */
async function dataURLToBlob(dataURL: string): Promise<Blob> {
  const response = await fetch(dataURL);
  return response.blob();
}

/**
 * Compress an image before upload
 * @param dataURL - Base64 data URL of the image
 * @param maxWidth - Maximum width (default 1920)
 * @param maxHeight - Maximum height (default 1920)
 * @param quality - JPEG quality 0-1 (default 0.8)
 * @returns Promise<string> - Compressed image as data URL
 */
export async function compressImage(
  dataURL: string,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.8
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to compressed JPEG
      const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataURL);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataURL;
  });
}

/**
 * Upload multiple photos with compression
 * @param orgId - Organization ID
 * @param inspectionId - Inspection ID
 * @param photoDataArray - Array of base64 data URLs
 * @returns Promise<string[]> - Array of download URLs
 */
export async function uploadPhotosWithCompression(
  orgId: string,
  inspectionId: string,
  photoDataArray: string[]
): Promise<string[]> {
  const uploadPromises = photoDataArray.map(async (photoData, index) => {
    // Compress image before upload
    const compressed = await compressImage(photoData);
    return uploadPhoto(orgId, inspectionId, compressed, index);
  });

  return Promise.all(uploadPromises);
}

/**
 * Upload a FLOC location photo
 * @param orgId - Organization ID
 * @param flocId - FLOC ID
 * @param file - Image file to upload
 * @returns Promise<string> - Download URL of the uploaded photo
 */
export async function uploadFlocLocationPhoto(
  orgId: string,
  flocId: string,
  file: File
): Promise<string> {
  try {
    const timestamp = Date.now();
    const filename = `location-${timestamp}.jpg`;
    const storagePath = `organizations/${orgId}/flocs/${flocId}/photos/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload FLOC location photo:', error);
    throw new Error('Failed to upload location photo');
  }
}

/**
 * Upload a FLOC document
 * @param orgId - Organization ID
 * @param flocId - FLOC ID
 * @param file - Document file to upload
 * @returns Promise<string> - Download URL of the uploaded document
 */
export async function uploadFlocDocument(
  orgId: string,
  flocId: string,
  file: File
): Promise<string> {
  try {
    const timestamp = Date.now();
    const filename = `${timestamp}-${file.name}`;
    const storagePath = `organizations/${orgId}/flocs/${flocId}/documents/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload FLOC document:', error);
    throw new Error('Failed to upload document');
  }
}

/**
 * Upload an asset photo
 * @param orgId - Organization ID
 * @param assetId - Asset ID
 * @param file - Image file to upload
 * @returns Promise<string> - Download URL of the uploaded photo
 */
export async function uploadAssetPhoto(
  orgId: string,
  assetId: string,
  file: File
): Promise<string> {
  try {
    const timestamp = Date.now();
    const filename = `photo-${timestamp}.jpg`;
    const storagePath = `organizations/${orgId}/assets/${assetId}/photos/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload asset photo:', error);
    throw new Error('Failed to upload photo');
  }
}

/**
 * Upload an asset document
 * @param orgId - Organization ID
 * @param assetId - Asset ID
 * @param file - Document file to upload
 * @returns Promise<string> - Download URL of the uploaded document
 */
export async function uploadAssetDocument(
  orgId: string,
  assetId: string,
  file: File
): Promise<string> {
  try {
    const timestamp = Date.now();
    const filename = `${timestamp}-${file.name}`;
    const storagePath = `organizations/${orgId}/assets/${assetId}/documents/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload asset document:', error);
    throw new Error('Failed to upload document');
  }
}

/**
 * Upload an asset audio recording
 * @param orgId - Organization ID
 * @param assetId - Asset ID
 * @param blob - Audio blob to upload
 * @param category - Recording category
 * @returns Promise<string> - Download URL of the uploaded audio
 */
export async function uploadAssetAudio(
  orgId: string,
  assetId: string,
  blob: Blob,
  category: string = 'other'
): Promise<string> {
  try {
    const timestamp = Date.now();
    const filename = `audio-${category}-${timestamp}.webm`;
    const storagePath = `organizations/${orgId}/assets/${assetId}/audio/${filename}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: 'audio/webm',
    });

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload asset audio:', error);
    throw new Error('Failed to upload audio recording');
  }
}
